import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { templateApi, apiError } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Page, Button, Badge, Field, Section, Loading } from "../../Components/UI/ui";
import { GATE, RESULT_TYPES, RANGE_SEX, daysToYears, yearsToDays, cap, money } from "../../lib/lab";

const blankRange = () => ({ sex: "any", ageMinY: "", ageMaxY: "", low: "", high: "", criticalLow: "", criticalHigh: "", text: "" });
const blankParam = () => ({ _k: Math.random(), code: "", name: "", unit: "", resultType: "numeric", decimals: 2, options: "", abnormalOptions: "", formula: "", method: "", isRequired: true, ranges: [blankRange()], open: true });
const blankSection = () => ({ _k: Math.random(), title: "", parameters: [blankParam()] });

const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

// API → editable form state (ages shown in years)
const toForm = (t) => ({
  name: t?.name || "", shortName: t?.shortName || "", category: t?.category || "", specimenType: t?.specimenType || "",
  method: t?.method || "", reportNotes: t?.reportNotes || "",
  sections: (t?.sections?.length ? t.sections : [blankSection()]).map((s) => ({
    _k: Math.random(), title: s.title || "",
    parameters: (s.parameters || []).map((p) => ({
      _k: Math.random(), code: p.code || "", name: p.name || "", unit: p.unit || "", resultType: p.resultType || "numeric",
      decimals: p.decimals ?? 2, options: (p.options || []).join(", "), abnormalOptions: (p.abnormalOptions || []).join(", "),
      formula: p.formula || "", method: p.method || "", isRequired: p.isRequired !== false, testNameId: p.testNameId, canonicalName: p.canonicalName,
      open: false,
      ranges: (p.referenceRanges || []).map((r) => ({
        sex: r.sex || "any", ageMinY: daysToYears(r.ageMinDays ?? 0), ageMaxY: r.ageMaxDays == null ? "" : daysToYears(r.ageMaxDays),
        low: r.low ?? "", high: r.high ?? "", criticalLow: r.criticalLow ?? "", criticalHigh: r.criticalHigh ?? "", text: r.text || "",
      })),
    })),
  })),
});

// form state → PUT/POST body
const toBody = (f) => ({
  name: f.name.trim(), shortName: f.shortName.trim(), category: f.category.trim(), specimenType: f.specimenType.trim(),
  method: f.method.trim(), reportNotes: f.reportNotes.trim(),
  sections: f.sections.map((s, si) => ({
    title: s.title.trim(), order: si,
    parameters: s.parameters.map((p, pi) => {
      const split = (v) => v.split(",").map((x) => x.trim()).filter(Boolean);
      const out = {
        code: p.code.trim().toUpperCase(), name: p.name.trim(), unit: p.unit.trim(), resultType: p.resultType,
        decimals: Number(p.decimals) || 0, method: p.method.trim(), isRequired: !!p.isRequired, order: pi,
        testNameId: p.testNameId, canonicalName: p.canonicalName,
        referenceRanges: p.resultType === "text" ? [] : p.ranges
          .filter((r) => r.low !== "" || r.high !== "" || r.criticalLow !== "" || r.criticalHigh !== "" || r.text)
          .map((r) => ({
            sex: r.sex, ageMinDays: yearsToDays(r.ageMinY) ?? 0, ageMaxDays: yearsToDays(r.ageMaxY),
            low: numOrNull(r.low), high: numOrNull(r.high), criticalLow: numOrNull(r.criticalLow), criticalHigh: numOrNull(r.criticalHigh),
            text: r.text.trim() || null,
          })),
      };
      if (p.resultType === "select") { out.options = split(p.options); out.abnormalOptions = split(p.abnormalOptions); }
      if (p.resultType === "calculated") out.formula = p.formula.trim();
      return out;
    }),
  })),
});

const TemplateEditor = () => {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const { can } = useAuth();
  const { toast, skus, loadSkus, invalidateTemplates } = useApp();
  const canWrite = can(...GATE.templatesWrite);
  const [tpl, setTpl] = useState(null);
  const [f, setF] = useState(isNew ? toForm(null) : null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [linkSku, setLinkSku] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try { const t = await templateApi.get(id); setTpl(t); setF(toForm(t)); }
    catch (e) { toast.error(e); navigate("/tests"); }
    finally { setLoading(false); }
  }, [id, isNew, toast, navigate]);

  useEffect(() => { load(); loadSkus(); }, [load, loadSkus]);

  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const setSection = (si, patch) => set({ sections: f.sections.map((s, i) => (i === si ? { ...s, ...patch } : s)) });
  const setParam = (si, pi, patch) => setSection(si, { parameters: f.sections[si].parameters.map((p, i) => (i === pi ? { ...p, ...patch } : p)) });
  const setRange = (si, pi, ri, patch) => setParam(si, pi, { ranges: f.sections[si].parameters[pi].ranges.map((r, i) => (i === ri ? { ...r, ...patch } : r)) });
  const moveParam = (si, pi, dir) => {
    const ps = [...f.sections[si].parameters];
    const j = pi + dir;
    if (j < 0 || j >= ps.length) return;
    [ps[pi], ps[j]] = [ps[j], ps[pi]];
    setSection(si, { parameters: ps });
  };

  const save = async () => {
    setErr(null);
    if (!f.name.trim()) return setErr("Name is required");
    const codes = f.sections.flatMap((s) => s.parameters.map((p) => p.code.trim().toUpperCase()));
    if (codes.some((c) => !c)) return setErr("Every parameter needs a code");
    if (new Set(codes).size !== codes.length) return setErr("Parameter codes must be unique");
    if (f.sections.flatMap((s) => s.parameters).some((p) => !p.name.trim())) return setErr("Every parameter needs a name");
    setSaving(true);
    try {
      const body = toBody(f);
      invalidateTemplates();
      if (isNew) {
        const t = await templateApi.create(body);
        toast.success("Definition created");
        navigate(`/tests/${t._id}`, { replace: true });
      } else {
        const t = await templateApi.update(id, body);
        setTpl(t); setF(toForm(t));
        toast.success(t.version !== tpl?.version ? `Saved · now version ${t.version}` : "Saved");
      }
    } catch (e) { setErr(apiError(e)); } finally { setSaving(false); }
  };

  const toggle = async () => {
    setBusy("toggle");
    try { invalidateTemplates(); const t = await templateApi.toggle(id); setTpl((x) => ({ ...x, isActive: t.isActive ?? !x.isActive })); toast.success(t.isActive ? "Activated" : "Deactivated"); }
    catch (e) { toast.error(e); } finally { setBusy(null); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this definition? Not possible once any result exists.")) return;
    setBusy("delete");
    try { await templateApi.remove(id); invalidateTemplates(); toast.success("Deleted"); navigate("/tests"); }
    catch (e) { toast.error(e); } finally { setBusy(null); }
  };
  const doLink = async () => {
    if (!linkSku) return;
    setBusy("link");
    try { await templateApi.linkSku(id, linkSku); setLinkSku(""); toast.success("SKU linked"); load(); }
    catch (e) { toast.error(e); } finally { setBusy(null); }
  };
  const doUnlink = async (skuId) => {
    setBusy(skuId);
    try { await templateApi.unlinkSku(id, skuId); toast.success("SKU unlinked"); load(); }
    catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  if (loading || !f) return <Page title="Test definition"><Loading /></Page>;
  const ro = !canWrite;
  const linkedIds = new Set((tpl?.linkedSkus || []).map((s) => s._id));

  return (
    <Page
      title={isNew ? "New test definition" : f.name || "Test definition"}
      subtitle={tpl ? `Version ${tpl.version} · ${tpl.isActive ? "active" : "inactive"}${tpl.source?.refType && tpl.source.refType !== "manual" ? ` · from global ${tpl.source.refType === "TestPanel" ? "panel" : "test"}` : ""}` : "Define parameters, units and reference ranges"}
      actions={
        <>
          <Button onClick={() => navigate("/tests")}>Back</Button>
          {canWrite && !isNew && <Button loading={busy === "toggle"} onClick={toggle}>{tpl?.isActive ? "Deactivate" : "Activate"}</Button>}
          {can(...GATE.templatesDelete) && !isNew && <Button variant="danger" loading={busy === "delete"} onClick={remove}>Delete</Button>}
          {canWrite && <Button variant="primary" loading={saving} onClick={save}>{isNew ? "Create" : "Save"}</Button>}
        </>
      }
    >
      {err && <div className="lab-danger-text">{err}</div>}
      <div className="lab-page__body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Section title="Test">
          <div className="lab-grid cols-4">
            <Field label="Name"><input className="lab-input" value={f.name} disabled={ro} onChange={(e) => set({ name: e.target.value })} /></Field>
            <Field label="Short name"><input className="lab-input" value={f.shortName} disabled={ro} onChange={(e) => set({ shortName: e.target.value })} placeholder="CBC" /></Field>
            <Field label="Category / department"><input className="lab-input" value={f.category} disabled={ro} onChange={(e) => set({ category: e.target.value })} placeholder="Hematology" /></Field>
            <Field label="Specimen"><input className="lab-input" value={f.specimenType} disabled={ro} onChange={(e) => set({ specimenType: e.target.value })} placeholder="Blood" /></Field>
            <Field label="Default method"><input className="lab-input" value={f.method} disabled={ro} onChange={(e) => set({ method: e.target.value })} /></Field>
            <Field label="Report notes (printed under the table)" className="span3"><input className="lab-input" value={f.reportNotes} disabled={ro} onChange={(e) => set({ reportNotes: e.target.value })} /></Field>
          </div>
        </Section>

        {f.sections.map((s, si) => (
          <Section
            key={s._k}
            title={
              <span className="lab-row" style={{ flex: 1 }}>
                <input className="lab-input sm" style={{ width: 260 }} placeholder={`Section ${si + 1} title (optional)`} value={s.title} disabled={ro} onChange={(e) => setSection(si, { title: e.target.value })} />
                <span className="lab-muted">{s.parameters.length} parameter{s.parameters.length === 1 ? "" : "s"}</span>
              </span>
            }
            right={!ro && (
              <span className="lab-row">
                <Button size="xs" onClick={() => setSection(si, { parameters: [...s.parameters, blankParam()] })}>+ Parameter</Button>
                <Button size="xs" variant="ghost" disabled={f.sections.length === 1} onClick={() => set({ sections: f.sections.filter((_, i) => i !== si) })}>Remove section</Button>
              </span>
            )}
          >
            <table className="lab-lines">
              <thead>
                <tr><th style={{ width: 90 }}>Code</th><th>Name</th><th style={{ width: 90 }}>Unit</th><th style={{ width: 120 }}>Type</th><th style={{ width: 60 }}>Dec.</th><th style={{ width: 70 }}>Req.</th><th style={{ width: 150 }}>Ranges</th><th style={{ width: 110 }} /></tr>
              </thead>
              <tbody>
                {s.parameters.map((p, pi) => (
                  <React.Fragment key={p._k}>
                    <tr className="lab-param-row">
                      <td><input className="lab-input sm" style={{ width: 80, textTransform: "uppercase" }} value={p.code} disabled={ro} onChange={(e) => setParam(si, pi, { code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "") })} /></td>
                      <td><input className="lab-input sm w-full" value={p.name} disabled={ro} onChange={(e) => setParam(si, pi, { name: e.target.value })} />
                        {p.canonicalName && <div className="lab-muted" style={{ fontSize: 11 }}>global: {p.canonicalName}</div>}</td>
                      <td><input className="lab-input sm" style={{ width: 80 }} value={p.unit} disabled={ro} onChange={(e) => setParam(si, pi, { unit: e.target.value })} /></td>
                      <td>
                        <select className="lab-select sm" value={p.resultType} disabled={ro} onChange={(e) => setParam(si, pi, { resultType: e.target.value })}>
                          {RESULT_TYPES.map((t) => <option key={t} value={t}>{cap(t)}</option>)}
                        </select>
                      </td>
                      <td>{(p.resultType === "numeric" || p.resultType === "calculated") && <input type="number" min="0" max="6" className="lab-input sm" style={{ width: 55 }} value={p.decimals} disabled={ro} onChange={(e) => setParam(si, pi, { decimals: e.target.value })} />}</td>
                      <td>{p.resultType !== "calculated" && <input type="checkbox" checked={p.isRequired} disabled={ro} onChange={(e) => setParam(si, pi, { isRequired: e.target.checked })} />}</td>
                      <td>
                        {p.resultType === "text" ? <span className="lab-muted">n/a</span> : (
                          <button type="button" className="lab-link" onClick={() => setParam(si, pi, { open: !p.open })}>
                            {p.ranges.filter((r) => r.low !== "" || r.high !== "" || r.text).length} band{p.ranges.length === 1 ? "" : "s"} {p.open ? "▴" : "▾"}
                          </button>
                        )}
                      </td>
                      <td>
                        {!ro && (
                          <span className="lab-row" style={{ gap: 2 }}>
                            <Button size="xs" variant="ghost" onClick={() => moveParam(si, pi, -1)} disabled={pi === 0}>↑</Button>
                            <Button size="xs" variant="ghost" onClick={() => moveParam(si, pi, 1)} disabled={pi === s.parameters.length - 1}>↓</Button>
                            <Button size="xs" variant="ghost" onClick={() => setSection(si, { parameters: s.parameters.filter((_, i) => i !== pi) })}>✕</Button>
                          </span>
                        )}
                      </td>
                    </tr>
                    {(p.resultType === "select" || p.resultType === "calculated" || p.method || p.open) && (
                      <tr>
                        <td colSpan={8} style={{ background: "#fafcfd" }}>
                          <div className="lab-grid cols-3" style={{ marginBottom: p.open && p.resultType !== "text" ? 8 : 0 }}>
                            {p.resultType === "select" && (
                              <>
                                <Field label="Options (comma separated)"><input className="lab-input sm w-full" value={p.options} disabled={ro} onChange={(e) => setParam(si, pi, { options: e.target.value })} placeholder="Negative, Trace, 1+, 2+" /></Field>
                                <Field label="Abnormal options (flagged A)"><input className="lab-input sm w-full" value={p.abnormalOptions} disabled={ro} onChange={(e) => setParam(si, pi, { abnormalOptions: e.target.value })} placeholder="Trace, 1+, 2+" /></Field>
                              </>
                            )}
                            {p.resultType === "calculated" && (
                              <Field label="Formula (codes, + - * / parentheses)"><input className="lab-input sm w-full" value={p.formula} disabled={ro} onChange={(e) => setParam(si, pi, { formula: e.target.value })} placeholder="HB / RBC * 10" /></Field>
                            )}
                            {p.open && <Field label="Method (overrides default)"><input className="lab-input sm w-full" value={p.method} disabled={ro} onChange={(e) => setParam(si, pi, { method: e.target.value })} /></Field>}
                          </div>
                          {p.open && p.resultType !== "text" && (
                            <>
                              <table className="lab-ranges">
                                <thead><tr><th>Sex</th><th>Age from (y)</th><th>Age to (y)</th><th>Low</th><th>High</th><th>Crit. low</th><th>Crit. high</th><th>Text range</th><th /></tr></thead>
                                <tbody>
                                  {p.ranges.map((r, ri) => (
                                    <tr key={ri}>
                                      <td><select className="lab-select" value={r.sex} disabled={ro} onChange={(e) => setRange(si, pi, ri, { sex: e.target.value })}>{RANGE_SEX.map((x) => <option key={x} value={x}>{x}</option>)}</select></td>
                                      <td><input type="number" min="0" step="0.01" className="lab-input" value={r.ageMinY} disabled={ro} onChange={(e) => setRange(si, pi, ri, { ageMinY: e.target.value })} placeholder="0" /></td>
                                      <td><input type="number" min="0" step="0.01" className="lab-input" value={r.ageMaxY} disabled={ro} onChange={(e) => setRange(si, pi, ri, { ageMaxY: e.target.value })} placeholder="∞" /></td>
                                      <td><input type="number" step="any" className="lab-input" value={r.low} disabled={ro} onChange={(e) => setRange(si, pi, ri, { low: e.target.value })} /></td>
                                      <td><input type="number" step="any" className="lab-input" value={r.high} disabled={ro} onChange={(e) => setRange(si, pi, ri, { high: e.target.value })} /></td>
                                      <td><input type="number" step="any" className="lab-input" value={r.criticalLow} disabled={ro} onChange={(e) => setRange(si, pi, ri, { criticalLow: e.target.value })} /></td>
                                      <td><input type="number" step="any" className="lab-input" value={r.criticalHigh} disabled={ro} onChange={(e) => setRange(si, pi, ri, { criticalHigh: e.target.value })} /></td>
                                      <td><input className="lab-input wide" value={r.text} disabled={ro} onChange={(e) => setRange(si, pi, ri, { text: e.target.value })} placeholder="Negative" /></td>
                                      <td>{!ro && <Button size="xs" variant="ghost" onClick={() => setParam(si, pi, { ranges: p.ranges.filter((_, i) => i !== ri) })}>✕</Button>}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {!ro && <Button size="xs" style={{ marginTop: 6 }} onClick={() => setParam(si, pi, { ranges: [...p.ranges, blankRange()] })}>+ Band</Button>}
                              <div className="lab-muted" style={{ fontSize: 11, marginTop: 4 }}>A sex-specific band beats "any"; a bounded age band beats an open one. 18 years = 6570 days on the server.</div>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </Section>
        ))}
        {!ro && <div><Button onClick={() => set({ sections: [...f.sections, blankSection()] })}>+ Section</Button></div>}

        {!isNew && (
          <Section title="Sold as (linked catalogue items)">
            <div className="lab-row" style={{ marginBottom: 8 }}>
              {(tpl?.linkedSkus || []).length === 0 && <span className="lab-muted">Not linked to any SKU yet — bookings of a SKU without a definition produce no result sheet.</span>}
              {(tpl?.linkedSkus || []).map((s) => (
                <span key={s._id} className="lab-tag">
                  {s.name} · {cap(s.kind)} · {money(s.unitPrice)}{s.isActive === false ? " · inactive" : ""}
                  {canWrite && <button type="button" className="lab-link" style={{ marginLeft: 6 }} onClick={() => doUnlink(s._id)} disabled={busy === s._id}>✕</button>}
                </span>
              ))}
            </div>
            {canWrite && (
              <div className="lab-row">
                <select className="lab-select" value={linkSku} onChange={(e) => setLinkSku(e.target.value)}>
                  <option value="">Link a catalogue item…</option>
                  {skus.filter((s) => s.serviceType === "pathology" && !linkedIds.has(s._id)).map((s) => <option key={s._id} value={s._id}>{s.name} ({cap(s.kind)})</option>)}
                </select>
                <Button size="sm" disabled={!linkSku} loading={busy === "link"} onClick={doLink}>Link</Button>
                {tpl?.linkedSkus?.length > 0 && <Badge kind="on">{tpl.linkedSkus.length} linked</Badge>}
              </div>
            )}
          </Section>
        )}
      </div>
    </Page>
  );
};

export default TemplateEditor;
